import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import {
  TrendingUp, TrendingDown, Users, UserMinus, Building2, Calendar,
  AlertTriangle, CheckCircle, Clock, FileText, Shield, RefreshCw,
  PieChart as PieChartIcon, BarChart3, Activity, Target, Percent, UserPlus, IndianRupee, Search
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { Input } from '../components/ui/input';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];

const HRAnalytics = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('12months');
  
  // Data states
  const [attritionData, setAttritionData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [demographicsData, setDemographicsData] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [summaryStats, setSummaryStats] = useState(null);
  const [cohortData, setCohortData] = useState(null);
  const [cohortYear, setCohortYear] = useState(new Date().getFullYear());
  const [salaryData, setSalaryData] = useState(null);
  const [salaryFromMonth, setSalaryFromMonth] = useState(1);
  const [salaryFromYear, setSalaryFromYear] = useState(new Date().getFullYear());
  const [salaryToMonth, setSalaryToMonth] = useState(new Date().getMonth() + 1);
  const [salaryToYear, setSalaryToYear] = useState(new Date().getFullYear());
  const [salarySearch, setSalarySearch] = useState('');
  const [salaryDeptFilter, setSalaryDeptFilter] = useState('all');

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  useEffect(() => {
    fetchCohortData();
  }, [cohortYear]);

  useEffect(() => {
    fetchSalaryData();
  }, [salaryFromMonth, salaryFromYear, salaryToMonth, salaryToYear]);

  const fetchSalaryData = async () => {
    try {
      const res = await api.get(`/hr-analytics/salary-analysis?from_month=${salaryFromMonth}&from_year=${salaryFromYear}&to_month=${salaryToMonth}&to_year=${salaryToYear}`);
      setSalaryData(res.data);
    } catch (err) {
      console.error('Failed to fetch salary data:', err);
    }
  };

  const fetchCohortData = async () => {
    try {
      const res = await api.get(`/hr-analytics/cohort-summary?year=${cohortYear}`);
      setCohortData(res.data);
    } catch (err) {
      console.error('Failed to fetch cohort data:', err);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/hr-analytics?date_range=${dateRange}`);
      const data = response.data;
      
      setAttritionData(data.attrition);
      setEngagementData(data.engagement);
      setDemographicsData(data.demographics);
      setComplianceData(data.compliance);
      setSummaryStats(data.summary);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;
  const formatNumber = (value) => (value || 0).toLocaleString('en-IN');

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-medium text-slate-900">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {typeof entry.value === 'number' && entry.value < 1 ? formatPercent(entry.value * 100) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="hr-analytics-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">HR Analytics Dashboard</h1>
            <p className="text-slate-500 text-sm">Workforce insights, attrition patterns, and compliance monitoring</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3months">Last 3 Months</SelectItem>
                <SelectItem value="6months">Last 6 Months</SelectItem>
                <SelectItem value="12months">Last 12 Months</SelectItem>
                <SelectItem value="24months">Last 24 Months</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAnalyticsData} className="gap-2">
              <RefreshCw size={16} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary KPIs */}
        {summaryStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Users className="text-indigo-600" size={24} />
                  {summaryStats.employee_growth >= 0 ? (
                    <TrendingUp className="text-emerald-500" size={16} />
                  ) : (
                    <TrendingDown className="text-red-500" size={16} />
                  )}
                </div>
                <p className="text-2xl font-bold text-indigo-900 mt-2">{formatNumber(summaryStats.total_employees)}</p>
                <p className="text-xs text-indigo-600">Total Employees</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <UserMinus className="text-red-600" size={24} />
                  <span className="text-xs font-medium text-red-700 bg-red-200 px-2 py-0.5 rounded">
                    {formatPercent(summaryStats.attrition_rate)}
                  </span>
                </div>
                <p className="text-2xl font-bold text-red-900 mt-2">{summaryStats.total_exits}</p>
                <p className="text-xs text-red-600">Monthly Attrition</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <CheckCircle className="text-emerald-600" size={24} />
                  <Percent className="text-emerald-500" size={16} />
                </div>
                <p className="text-2xl font-bold text-emerald-900 mt-2">{formatPercent(summaryStats.attendance_rate)}</p>
                <p className="text-xs text-emerald-600">Attendance Rate</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Clock className="text-amber-600" size={24} />
                </div>
                <p className="text-2xl font-bold text-amber-900 mt-2">{summaryStats.avg_tenure} yrs</p>
                <p className="text-xs text-amber-600">Avg. Tenure</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Shield className="text-purple-600" size={24} />
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    summaryStats.compliance_score >= 90 ? 'bg-emerald-200 text-emerald-700' :
                    summaryStats.compliance_score >= 70 ? 'bg-amber-200 text-amber-700' :
                    'bg-red-200 text-red-700'
                  }`}>
                    {summaryStats.compliance_score >= 90 ? 'Good' : summaryStats.compliance_score >= 70 ? 'Fair' : 'Low'}
                  </span>
                </div>
                <p className="text-2xl font-bold text-purple-900 mt-2">{formatPercent(summaryStats.compliance_score)}</p>
                <p className="text-xs text-purple-600">Compliance Score</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Building2 className="text-cyan-600" size={24} />
                </div>
                <p className="text-2xl font-bold text-cyan-900 mt-2">{summaryStats.total_departments}</p>
                <p className="text-xs text-cyan-600">Departments</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart3 size={16} />
              Overview
            </TabsTrigger>
            <TabsTrigger value="cohort" className="gap-2" data-testid="cohort-tab">
              <UserPlus size={16} />
              Cohort
            </TabsTrigger>
            <TabsTrigger value="salary" className="gap-2" data-testid="salary-tab">
              <IndianRupee size={16} />
              Salary
            </TabsTrigger>
            <TabsTrigger value="attrition" className="gap-2">
              <UserMinus size={16} />
              Attrition
            </TabsTrigger>
            <TabsTrigger value="engagement" className="gap-2">
              <Activity size={16} />
              Engagement
            </TabsTrigger>
            <TabsTrigger value="compliance" className="gap-2">
              <Shield size={16} />
              Compliance
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Attrition Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TrendingDown className="text-red-500" size={20} />
                    Monthly Attrition Rate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={Array.isArray(attritionData) ? attritionData : (attritionData?.monthly_trend || [])}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="rate" 
                        stroke="#EF4444" 
                        fill="#FEE2E2" 
                        name="Attrition Rate %"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Workforce Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <PieChartIcon className="text-indigo-500" size={20} />
                    Workforce by Department
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={demographicsData?.department || demographicsData?.by_department || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="value"
                        nameKey="name"
                        paddingAngle={2}
                      >
                        {(demographicsData?.department || demographicsData?.by_department || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} employees`, name]} />
                      <Legend 
                        layout="vertical" 
                        align="right" 
                        verticalAlign="middle"
                        formatter={(value, entry) => {
                          const data = demographicsData?.department || demographicsData?.by_department || [];
                          const item = data.find(d => d.name === value);
                          const total = data.reduce((sum, d) => sum + (d.value || d.count || 0), 0);
                          const percent = item && total > 0 ? ((item.value || item.count) / total * 100).toFixed(0) : 0;
                          // Truncate long names
                          const shortName = value.length > 15 ? value.substring(0, 15) + '...' : value;
                          return <span style={{ fontSize: '11px' }}>{shortName} ({percent}%)</span>;
                        }}
                        wrapperStyle={{ fontSize: '11px', paddingLeft: '10px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Attendance Overview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Calendar className="text-emerald-500" size={20} />
                    Attendance Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={engagementData?.attendance_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="present" fill="#10B981" name="Present" />
                      <Bar dataKey="absent" fill="#EF4444" name="Absent" />
                      <Line type="monotone" dataKey="rate" stroke="#4F46E5" name="Rate %" strokeWidth={2} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Compliance Overview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Shield className="text-purple-500" size={20} />
                    Compliance Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {complianceData?.categories?.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-slate-700">{item.name}</span>
                        <span className={`text-sm font-medium ${
                          item.score >= 90 ? 'text-emerald-600' :
                          item.score >= 70 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {formatPercent(item.score)}
                        </span>
                      </div>
                      <Progress 
                        value={item.score} 
                        className={`h-2 ${
                          item.score >= 90 ? '[&>div]:bg-emerald-500' :
                          item.score >= 70 ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'
                        }`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Cohort Tab */}
          <TabsContent value="cohort" className="space-y-6 mt-6" data-testid="cohort-content">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <UserPlus className="text-indigo-500" size={20} />
                      Monthly Cohort Summary
                    </CardTitle>
                    <CardDescription>Tracking joinees each month and their retention status</CardDescription>
                  </div>
                  <Select value={String(cohortYear)} onValueChange={(v) => setCohortYear(Number(v))}>
                    <SelectTrigger className="w-32" data-testid="cohort-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(5)].map((_, i) => {
                        const y = new Date().getFullYear() - i;
                        return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {cohortData ? (
                  <div className="overflow-x-auto" data-testid="cohort-table-wrapper">
                    <table className="w-full text-sm" data-testid="cohort-table">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-3 font-semibold text-slate-700 bg-slate-50">Month</th>
                          <th className="text-center py-3 px-3 font-semibold text-slate-700 bg-indigo-50">Joinees</th>
                          <th className="text-center py-3 px-2 font-semibold text-red-700 bg-red-50 text-xs">Left ≤7d</th>
                          <th className="text-center py-3 px-2 font-semibold text-red-600 bg-red-50 text-xs">Left ≤30d</th>
                          <th className="text-center py-3 px-2 font-semibold text-orange-600 bg-orange-50 text-xs">Left ≤60d</th>
                          <th className="text-center py-3 px-2 font-semibold text-amber-600 bg-amber-50 text-xs">Left ≤90d</th>
                          <th className="text-center py-3 px-2 font-semibold text-slate-600 bg-slate-50 text-xs">Left 120+d</th>
                          <th className="text-center py-3 px-3 font-semibold text-emerald-700 bg-emerald-50">Active</th>
                          <th className="text-center py-3 px-3 font-semibold text-rose-700 bg-rose-50 text-xs">Early Attrition %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cohortData.cohorts?.map((row, idx) => (
                          <tr key={idx} className={`border-b border-slate-100 ${row.joinees === 0 ? 'text-slate-400' : ''} hover:bg-slate-50 transition-colors`} data-testid={`cohort-row-${idx}`}>
                            <td className="py-2.5 px-3 font-medium text-slate-800">{row.month}</td>
                            <td className="text-center py-2.5 px-3">
                              <span className={`font-semibold ${row.joinees > 0 ? 'text-indigo-700' : ''}`}>{row.joinees}</span>
                            </td>
                            <td className="text-center py-2.5 px-2">
                              {row.left_7 > 0 ? <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium">{row.left_7}</span> : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="text-center py-2.5 px-2">
                              {row.left_30 > 0 ? <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded-full text-xs font-medium">{row.left_30}</span> : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="text-center py-2.5 px-2">
                              {row.left_60 > 0 ? <span className="bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full text-xs font-medium">{row.left_60}</span> : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="text-center py-2.5 px-2">
                              {row.left_90 > 0 ? <span className="bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full text-xs font-medium">{row.left_90}</span> : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="text-center py-2.5 px-2">
                              {row.left_120_plus > 0 ? <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-xs font-medium">{row.left_120_plus}</span> : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="text-center py-2.5 px-3">
                              {row.active > 0 ? <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold">{row.active}</span> : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="text-center py-2.5 px-3">
                              {row.joinees > 0 ? (
                                <span className={`font-semibold text-xs px-2 py-0.5 rounded-full ${
                                  row.early_attrition_pct >= 50 ? 'bg-red-100 text-red-700' :
                                  row.early_attrition_pct >= 25 ? 'bg-amber-100 text-amber-700' :
                                  row.early_attrition_pct > 0 ? 'bg-yellow-50 text-yellow-700' :
                                  'bg-emerald-50 text-emerald-700'
                                }`}>
                                  {row.early_attrition_pct}%
                                </span>
                              ) : <span className="text-slate-300">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {cohortData.totals && (
                        <tfoot>
                          <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300" data-testid="cohort-totals-row">
                            <td className="py-3 px-3 text-slate-900">Total</td>
                            <td className="text-center py-3 px-3 text-indigo-800">{cohortData.totals.joinees}</td>
                            <td className="text-center py-3 px-2 text-red-700">{cohortData.totals.left_7}</td>
                            <td className="text-center py-3 px-2 text-red-600">{cohortData.totals.left_30}</td>
                            <td className="text-center py-3 px-2 text-orange-600">{cohortData.totals.left_60}</td>
                            <td className="text-center py-3 px-2 text-amber-600">{cohortData.totals.left_90}</td>
                            <td className="text-center py-3 px-2 text-slate-600">{cohortData.totals.left_120_plus}</td>
                            <td className="text-center py-3 px-3 text-emerald-700">{cohortData.totals.active}</td>
                            <td className="text-center py-3 px-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                cohortData.totals.early_attrition_pct >= 50 ? 'bg-red-200 text-red-800' :
                                cohortData.totals.early_attrition_pct >= 25 ? 'bg-amber-200 text-amber-800' :
                                'bg-emerald-200 text-emerald-800'
                              }`}>
                                {cohortData.totals.early_attrition_pct}%
                              </span>
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">Loading cohort data...</div>
                )}
              </CardContent>
            </Card>

            {/* Cohort Visual Chart */}
            {cohortData && cohortData.cohorts?.some(c => c.joinees > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Cohort Retention Breakdown</CardTitle>
                  <CardDescription>Stacked view of joinee outcomes by month</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={cohortData.cohorts.filter(c => c.joinees > 0)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="active" stackId="a" fill="#10B981" name="Active" />
                      <Bar dataKey="left_7" stackId="a" fill="#EF4444" name="Left ≤7d" />
                      <Bar dataKey="left_30" stackId="a" fill="#F87171" name="Left ≤30d" />
                      <Bar dataKey="left_60" stackId="a" fill="#F97316" name="Left ≤60d" />
                      <Bar dataKey="left_90" stackId="a" fill="#F59E0B" name="Left ≤90d" />
                      <Bar dataKey="left_120_plus" stackId="a" fill="#94A3B8" name="Left 120+d" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Salary Analysis Tab */}
          <TabsContent value="salary" className="space-y-6 mt-6" data-testid="salary-content">
            {(() => {
              const formatINR = (v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v >= 1000 ? `${(v/1000).toFixed(1)}K` : Math.round(v);
              const formatFull = (v) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v);
              const allDepts = [...new Set(salaryData?.resources?.map(r => r.department).filter(Boolean) || [])];
              const months = salaryData?.months || [];

              // Apply search + department filter
              const filteredResources = salaryData?.resources?.filter(r => {
                const matchSearch = !salarySearch || r.name?.toLowerCase().includes(salarySearch.toLowerCase()) || r.employee_code?.toLowerCase().includes(salarySearch.toLowerCase());
                const matchDept = salaryDeptFilter === 'all' || r.department === salaryDeptFilter;
                return matchSearch && matchDept;
              }) || [];

              // Compute totals from filtered data
              const filteredGrandTotal = {
                gross: filteredResources.reduce((s, r) => s + (r.total_gross || 0), 0),
                deductions: filteredResources.reduce((s, r) => s + (r.total_deductions || 0), 0),
                net: filteredResources.reduce((s, r) => s + (r.total_net || 0), 0),
              };
              const filteredMonthlyTotals = {};
              months.forEach(m => { filteredMonthlyTotals[m] = { gross: 0, deductions: 0, net: 0, headcount: 0 }; });
              filteredResources.forEach(r => {
                Object.entries(r.months || {}).forEach(([m, d]) => {
                  if (filteredMonthlyTotals[m]) {
                    filteredMonthlyTotals[m].gross += d.gross || 0;
                    filteredMonthlyTotals[m].deductions += d.total_deductions || 0;
                    filteredMonthlyTotals[m].net += d.net || 0;
                    filteredMonthlyTotals[m].headcount += 1;
                  }
                });
              });
              const filteredDeptSummary = {};
              filteredResources.forEach(r => {
                const dept = r.department || 'Unknown';
                if (!filteredDeptSummary[dept]) filteredDeptSummary[dept] = { department: dept, headcount: 0, total_gross: 0, total_net: 0 };
                filteredDeptSummary[dept].headcount += 1;
                filteredDeptSummary[dept].total_gross += r.total_gross || 0;
                filteredDeptSummary[dept].total_net += r.total_net || 0;
              });
              const deptSummaryList = Object.values(filteredDeptSummary).sort((a, b) => b.total_gross - a.total_gross);

              return salaryData ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card><CardContent className="p-4 text-center">
                      <p className="text-xs text-slate-500 uppercase">Total Resources</p>
                      <p className="text-2xl font-bold text-indigo-700 mt-1">{filteredResources.length}</p>
                    </CardContent></Card>
                    <Card><CardContent className="p-4 text-center">
                      <p className="text-xs text-slate-500 uppercase">Total Gross</p>
                      <p className="text-2xl font-bold text-slate-800 mt-1">{formatFull(filteredGrandTotal.gross)}</p>
                    </CardContent></Card>
                    <Card><CardContent className="p-4 text-center">
                      <p className="text-xs text-slate-500 uppercase">Total Deductions</p>
                      <p className="text-2xl font-bold text-red-600 mt-1">{formatFull(filteredGrandTotal.deductions)}</p>
                    </CardContent></Card>
                    <Card><CardContent className="p-4 text-center">
                      <p className="text-xs text-slate-500 uppercase">Total Net Salary</p>
                      <p className="text-2xl font-bold text-emerald-700 mt-1">{formatFull(filteredGrandTotal.net)}</p>
                    </CardContent></Card>
                  </div>

                  {/* Date Range Filter */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm font-medium text-slate-600">Date Range:</span>
                        <div className="flex items-center gap-1.5">
                          <Select value={String(salaryFromMonth)} onValueChange={v => setSalaryFromMonth(Number(v))}>
                            <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Select value={String(salaryFromYear)} onValueChange={v => setSalaryFromYear(Number(v))}>
                            <SelectTrigger className="w-20 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[...Array(5)].map((_,i) => { const y = new Date().getFullYear() - i; return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}
                            </SelectContent>
                          </Select>
                        </div>
                        <span className="text-sm text-slate-400">to</span>
                        <div className="flex items-center gap-1.5">
                          <Select value={String(salaryToMonth)} onValueChange={v => setSalaryToMonth(Number(v))}>
                            <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Select value={String(salaryToYear)} onValueChange={v => setSalaryToYear(Number(v))}>
                            <SelectTrigger className="w-20 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[...Array(5)].map((_,i) => { const y = new Date().getFullYear() - i; return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-2 h-4 w-4 text-slate-400" />
                            <Input placeholder="Search name/code..." value={salarySearch} onChange={e => setSalarySearch(e.target.value)} className="pl-9 w-44 h-8 text-xs" data-testid="salary-search" />
                          </div>
                          <Select value={salaryDeptFilter} onValueChange={setSalaryDeptFilter}>
                            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Department" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Departments</SelectItem>
                              {allDepts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Monthly Cost Trend Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Monthly Salary Cost Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <ComposedChart data={months.map(m => ({ month: m, gross: filteredMonthlyTotals[m]?.gross || 0, net: filteredMonthlyTotals[m]?.net || 0, headcount: filteredMonthlyTotals[m]?.headcount || 0 }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="left" tickFormatter={v => formatINR(v)} tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v, name) => [name === 'headcount' ? v : `Rs. ${formatFull(v)}`, name === 'headcount' ? 'Headcount' : name === 'gross' ? 'Gross' : 'Net']} />
                          <Legend />
                          <Bar yAxisId="left" dataKey="gross" fill="#818CF8" name="Gross" radius={[3,3,0,0]} />
                          <Bar yAxisId="left" dataKey="net" fill="#34D399" name="Net" radius={[3,3,0,0]} />
                          <Line yAxisId="right" dataKey="headcount" stroke="#F59E0B" strokeWidth={2} name="Headcount" dot={{ r: 3 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Department Summary */}
                  {deptSummaryList.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle className="text-base">Department-wise Salary Summary</CardTitle></CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-200">
                                <th className="text-left py-2.5 px-3 font-semibold text-slate-700 bg-slate-50">Department</th>
                                <th className="text-center py-2.5 px-3 font-semibold text-slate-700 bg-slate-50">Headcount</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-slate-700 bg-slate-50">Total Gross</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-slate-700 bg-slate-50">Total Net</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-slate-700 bg-slate-50">Avg/Employee</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deptSummaryList.map((d, i) => (
                                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="py-2 px-3 font-medium">{d.department}</td>
                                  <td className="text-center py-2 px-3">{d.headcount}</td>
                                  <td className="text-right py-2 px-3">{formatFull(d.total_gross)}</td>
                                  <td className="text-right py-2 px-3 text-emerald-700 font-medium">{formatFull(d.total_net)}</td>
                                  <td className="text-right py-2 px-3 text-indigo-600">{formatFull(d.headcount ? Math.round(d.total_gross / d.headcount) : 0)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Resource-wise Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <IndianRupee className="text-indigo-500" size={18} />
                        Resource-wise Monthly Salary ({filteredResources.length} employees)
                      </CardTitle>
                      <CardDescription>Gross / Net salary per employee per month</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto" data-testid="salary-table-wrapper">
                        <table className="w-full text-xs" data-testid="salary-table">
                          <thead>
                            <tr className="border-b-2 border-slate-200">
                              <th className="text-left py-2.5 px-2 font-semibold text-slate-700 bg-slate-50 sticky left-0 z-10 min-w-[160px]">Employee</th>
                              {months.map(m => <th key={m} className="text-center py-2.5 px-1 font-semibold text-slate-600 bg-slate-50 min-w-[70px]">{m}</th>)}
                              <th className="text-right py-2.5 px-2 font-semibold text-slate-700 bg-indigo-50 min-w-[80px]">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredResources.map((r, idx) => (
                              <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`salary-row-${idx}`}>
                                <td className="py-2 px-2 sticky left-0 bg-white z-10">
                                  <p className="font-medium text-slate-800 truncate max-w-[150px]">{r.name}</p>
                                  <p className="text-[10px] text-slate-400">{r.employee_code} {r.department ? `| ${r.department}` : ''}</p>
                                </td>
                                {months.map(m => {
                                  const md = r.months[m];
                                  return (
                                    <td key={m} className="text-center py-2 px-1">
                                      {md ? (
                                        <div>
                                          <p className="font-medium text-slate-700">{formatINR(md.gross)}</p>
                                          <p className="text-emerald-600 text-[10px]">{formatINR(md.net)}</p>
                                        </div>
                                      ) : <span className="text-slate-300">-</span>}
                                    </td>
                                  );
                                })}
                                <td className="text-right py-2 px-2 bg-indigo-50/50">
                                  <p className="font-bold text-slate-800">{formatINR(r.total_gross)}</p>
                                  <p className="text-emerald-700 font-medium text-[10px]">{formatINR(r.total_net)}</p>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-slate-100 border-t-2 border-slate-300 font-semibold" data-testid="salary-totals-row">
                              <td className="py-2.5 px-2 sticky left-0 bg-slate-100 z-10 text-slate-900">Monthly Total</td>
                              {months.map(m => {
                                const mt = filteredMonthlyTotals[m] || {};
                                return (
                                  <td key={m} className="text-center py-2.5 px-1">
                                    {mt.headcount > 0 ? (
                                      <div>
                                        <p className="text-slate-800 font-bold">{formatINR(mt.gross)}</p>
                                        <p className="text-emerald-700 text-[10px]">{formatINR(mt.net)}</p>
                                        <p className="text-slate-400 text-[9px]">{mt.headcount} emp</p>
                                      </div>
                                    ) : <span className="text-slate-300">-</span>}
                                  </td>
                                );
                              })}
                              <td className="text-right py-2.5 px-2 bg-slate-200">
                                <p className="text-slate-900 font-bold">{formatINR(filteredGrandTotal.gross)}</p>
                                <p className="text-emerald-800 font-bold text-[10px]">{formatINR(filteredGrandTotal.net)}</p>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <div className="flex gap-4 mt-3 text-[10px] text-slate-400">
                        <span>Top line: Gross Salary</span>
                        <span className="text-emerald-500">Bottom line: Net Salary</span>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <div className="text-center py-12 text-slate-400">Loading salary analysis...</div>
              );
            })()}
          </TabsContent>

          {/* Attrition Tab */}
          <TabsContent value="attrition" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Attrition by Reason */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Attrition by Reason</CardTitle>
                  <CardDescription>Exit reasons distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={attritionData?.by_reason || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="reason" type="category" tick={{ fontSize: 12 }} width={120} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" fill="#EF4444" name="Exits" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Attrition Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Key Metrics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-red-600">Monthly Attrition Rate</p>
                    <p className="text-3xl font-bold text-red-700">{formatPercent(attritionData?.monthly_rate || 0)}</p>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <p className="text-sm text-amber-600">Annual Attrition Rate</p>
                    <p className="text-3xl font-bold text-amber-700">{formatPercent(attritionData?.annual_rate || 0)}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-600">Avg. Tenure at Exit</p>
                    <p className="text-3xl font-bold text-slate-700">{attritionData?.avg_tenure_at_exit || 0} yrs</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Attrition by Department */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Attrition by Department</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={attritionData?.by_department || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="department" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="exits" fill="#EF4444" name="Exits" />
                    <Bar dataKey="headcount" fill="#4F46E5" name="Headcount" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Monthly Trend with Forecast */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Attrition Trend</CardTitle>
                <CardDescription>Monthly exits and attrition rate</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={attritionData?.monthly_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="exits" stroke="#EF4444" name="Exits" strokeWidth={2} dot />
                    <Line yAxisId="right" type="monotone" dataKey="rate" stroke="#F59E0B" name="Rate %" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Engagement Tab */}
          <TabsContent value="engagement" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Leave Usage */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Leave Utilization</CardTitle>
                  <CardDescription>Leave type distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={engagementData?.leave_usage || []}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="days"
                        nameKey="type"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {(engagementData?.leave_usage || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Attendance by Department */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Attendance by Department</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={engagementData?.attendance_by_dept || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="department" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="rate" fill="#10B981" name="Attendance %" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Monthly Attendance Trend */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Monthly Attendance Pattern</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={engagementData?.attendance_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area type="monotone" dataKey="present" stackId="1" stroke="#10B981" fill="#D1FAE5" name="Present" />
                      <Area type="monotone" dataKey="leave" stackId="1" stroke="#F59E0B" fill="#FEF3C7" name="Leave" />
                      <Area type="monotone" dataKey="absent" stackId="1" stroke="#EF4444" fill="#FEE2E2" name="Absent" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Demographics */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Age Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={demographicsData?.by_age || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8B5CF6" name="Employees" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Gender Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={demographicsData?.by_gender || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        dataKey="count"
                        nameKey="gender"
                        label
                      >
                        {(demographicsData?.by_gender || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#4F46E5' : '#EC4899'} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tenure Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={demographicsData?.by_tenure || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#06B6D4" name="Employees" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance" className="space-y-6 mt-6">
            {/* Compliance Score Card */}
            <Card className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100">Overall Compliance Score</p>
                    <p className="text-5xl font-bold mt-2">{formatPercent(complianceData?.overall_score || 0)}</p>
                    <p className="text-purple-200 mt-1">
                      {complianceData?.overall_score >= 90 ? 'Excellent' :
                       complianceData?.overall_score >= 70 ? 'Good' : 'Needs Improvement'}
                    </p>
                  </div>
                  <Shield size={80} className="text-white/30" />
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Compliance Categories */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Compliance by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={complianceData?.categories || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={120} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="score" name="Score %" radius={[0, 4, 4, 0]}>
                        {(complianceData?.categories || []).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.score >= 90 ? '#10B981' : entry.score >= 70 ? '#F59E0B' : '#EF4444'} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Alerts */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <AlertTriangle className="text-amber-500" size={20} />
                    Compliance Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 max-h-[300px] overflow-y-auto">
                  {complianceData?.alerts?.length > 0 ? (
                    complianceData.alerts.map((alert, index) => (
                      <div 
                        key={index}
                        className={`p-3 rounded-lg border-l-4 ${
                          alert.severity === 'high' ? 'bg-red-50 border-red-500' :
                          alert.severity === 'medium' ? 'bg-amber-50 border-amber-500' :
                          'bg-blue-50 border-blue-500'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{alert.title}</p>
                            <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                          </div>
                          <Badge variant={
                            alert.severity === 'high' ? 'destructive' :
                            alert.severity === 'medium' ? 'warning' : 'secondary'
                          }>
                            {alert.severity}
                          </Badge>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle size={48} className="mx-auto mb-2 text-emerald-500" />
                      <p>No compliance alerts</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Document Compliance */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Document Compliance Status</CardTitle>
                <CardDescription>Employee document verification status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {complianceData?.document_status?.map((doc, index) => (
                    <div key={index} className="bg-slate-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <FileText size={20} className="text-slate-400" />
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          doc.compliance >= 90 ? 'bg-emerald-100 text-emerald-700' :
                          doc.compliance >= 70 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {formatPercent(doc.compliance)}
                        </span>
                      </div>
                      <p className="font-medium text-slate-900">{doc.type}</p>
                      <p className="text-xs text-slate-500">{doc.verified}/{doc.total} verified</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default HRAnalytics;
